/* eslint-disable max-lines */
import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { BidItemStatus, ComplianceSnippet, FileSourceSnippet } from "../../types"

import { ChevronDown, ChevronRight } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { COMPLIANCE_CONFIG } from "./bid-review-constants"
import {
  CitationText,
  type DocumentNameLookup,
  type ExtractionItemLookup,
  type OpenSourceFn,
  SourceButton,
} from "./extraction-citation"

// ── Status summary ─────────────────────────────────────────────────────────────

export function StatusSummary({ items }: { items: { status: BidItemStatus }[] }) {
  const { t } = useTranslation()
  const met = items.filter((i) => i.status === "met").length
  const partial = items.filter((i) => i.status === "partial").length
  const missing = items.filter((i) => i.status === "missing").length

  return (
    <div className="flex items-center gap-2 text-[12px]">
      {met > 0 && (
        <span className="text-success">
          {t("tender.bidReview.complianceSummary.met", { count: met })}
        </span>
      )}
      {partial > 0 && (
        <span className="text-warning">
          {t("tender.bidReview.complianceSummary.partial", { count: partial })}
        </span>
      )}
      {missing > 0 && (
        <span className="text-destructive">
          {t("tender.bidReview.complianceSummary.missing", { count: missing })}
        </span>
      )}
    </div>
  )
}

// ── Compliance snippet card (popover-internal) ─────────────────────────────────

type ComplianceSnippetCardProps = {
  snippet: ComplianceSnippet
  docNameLookup?: DocumentNameLookup
  onOpenSource?: OpenSourceFn
}

function resolveDocId(docName: string, docNameLookup?: DocumentNameLookup): string | undefined {
  if (!docNameLookup) return undefined
  const lower = docName.toLowerCase()
  return docNameLookup.get(lower) ?? docNameLookup.get(lower.replace(/\.[^/.]+$/, ""))
}

function ComplianceSnippetCard({
  snippet,
  docNameLookup,
  onOpenSource,
}: ComplianceSnippetCardProps) {
  const { t } = useTranslation()
  const docName = snippet.documentName || t("tender.bidReview.defaultBidDocument")
  const docId = resolveDocId(docName, docNameLookup)

  const navigableTo: FileSourceSnippet | undefined = docId
    ? {
        kind: "file",
        documentId: docId,
        documentName: docName,
        page: snippet.page ?? 0,
        quote: snippet.quote,
      }
    : undefined

  return (
    <div className="flex flex-col gap-2 py-1">
      <SourceButton
        documentName={docName}
        navigableTo={navigableTo}
        page={snippet.page}
        onOpenSource={onOpenSource}
      />
      {snippet.quote && (
        <div className="border-border/40 border-l-2 py-0.5 pl-3">
          <p className="text-muted-foreground line-clamp-3 text-[11px] leading-relaxed italic">
            "{snippet.quote}"
          </p>
        </div>
      )}
    </div>
  )
}

function SourceFallbackButton({
  documentName,
  page,
  docNameLookup,
  onOpenSource,
}: {
  documentName: string
  page?: number
  docNameLookup?: DocumentNameLookup
  onOpenSource?: OpenSourceFn
}) {
  const docId = resolveDocId(documentName, docNameLookup)
  const navigableTo = docId
    ? { kind: "file" as const, documentId: docId, documentName, page: page ?? 0 }
    : undefined
  return (
    <SourceButton
      documentName={documentName}
      navigableTo={navigableTo}
      page={page}
      onOpenSource={onOpenSource}
    />
  )
}

// ── Compliance evidence popover ────────────────────────────────────────────────

function confidenceDotClass(confidence: number): string {
  if (confidence >= 0.7) return "border border-foreground bg-success"
  if (confidence >= 0.4) return "border border-foreground bg-warning"
  return "border border-foreground bg-destructive"
}

type ComplianceEvidencePopoverProps = {
  reasoning?: string
  supportingSnippets?: ComplianceSnippet[]
  confidence: number
  extractionLookup?: ExtractionItemLookup
  docNameLookup?: DocumentNameLookup
  onOpenSource?: OpenSourceFn
}

function ComplianceEvidencePopover({
  reasoning,
  supportingSnippets,
  confidence,
  extractionLookup,
  docNameLookup,
  onOpenSource,
}: ComplianceEvidencePopoverProps) {
  const { t } = useTranslation()
  const snippets = supportingSnippets ?? []
  const hasContent = Boolean(reasoning) || snippets.length > 0
  if (!hasContent) return null

  const count = snippets.length
  let label: string
  if (count === 1) {
    label = t("tender.bidReview.popover.evidenceBadgeOne")
  } else if (count > 1) {
    label = t("tender.bidReview.popover.evidenceBadgeMany", { count })
  } else {
    label = t("tender.bidReview.popover.reasoning")
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "h-auto gap-1.5 rounded border px-2 py-1 text-xs font-medium",
            "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
            "data-[state=open]:bg-accent data-[state=open]:text-foreground"
          )}
        >
          <span className={cn("size-2 shrink-0 rounded-full", confidenceDotClass(confidence))} />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="border-border/60 bg-background/80 w-[360px] space-y-3 border p-4 shadow-xl backdrop-blur-md"
        sideOffset={6}
      >
        {reasoning && (
          <div className="space-y-2">
            <p className="text-muted-foreground text-xs font-medium">
              {t("tender.bidReview.popover.reasoning")}
            </p>
            {extractionLookup ? (
              <CitationText
                as="p"
                className="text-sm leading-relaxed"
                lookup={extractionLookup}
                text={reasoning}
                onOpenSource={onOpenSource}
              />
            ) : (
              <p className="text-sm leading-relaxed">{reasoning}</p>
            )}
          </div>
        )}
        {snippets.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium">
              {snippets.length === 1
                ? t("tender.bidReview.popover.source")
                : t("tender.bidReview.popover.sources")}
            </p>
            <div className="divide-border/30 divide-y">
              {snippets.map((snippet, i) => (
                <ComplianceSnippetCard
                  key={i}
                  docNameLookup={docNameLookup}
                  snippet={snippet}
                  onOpenSource={onOpenSource}
                />
              ))}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

// ── Shared expanded details ────────────────────────────────────────────────────

type ComplianceItemDetailsProps = {
  gapDescription?: string
  suggestedFix?: string
  extractionLookup?: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
}

function ComplianceItemDetails({
  gapDescription,
  suggestedFix,
  extractionLookup,
  onOpenSource,
}: ComplianceItemDetailsProps) {
  const { t } = useTranslation()
  return (
    <div className="mt-3 flex flex-col gap-2.5 pl-7">
      {gapDescription && (
        <div className="bg-warning/10 text-warning-foreground rounded-md px-3 py-2 text-[12px] leading-relaxed">
          <span className="font-medium">{t("tender.bidReview.popover.gapPrefix")}</span>
          {extractionLookup ? (
            <CitationText
              lookup={extractionLookup}
              text={gapDescription}
              onOpenSource={onOpenSource}
            />
          ) : (
            gapDescription
          )}
        </div>
      )}
      {suggestedFix && (
        <div className="bg-accent/10 text-accent-foreground rounded-md px-3 py-2 text-[12px] leading-relaxed">
          <span className="font-medium">{t("tender.bidReview.popover.actionPrefix")}</span>
          {extractionLookup ? (
            <CitationText
              lookup={extractionLookup}
              text={suggestedFix}
              onOpenSource={onOpenSource}
            />
          ) : (
            suggestedFix
          )}
        </div>
      )}
    </div>
  )
}

// ── Requirement item row ───────────────────────────────────────────────────────

export type RequirementItemRowProps = {
  id: string
  description: string
  status: BidItemStatus
  isMandatory?: boolean
  category?: string
  sourceDocument?: string
  sourcePage?: number
  reasoning?: string
  bidQuote?: string
  gapDescription?: string
  suggestedFix?: string
  supportingSnippets?: ComplianceSnippet[]
  confidence: number
  extractionLookup?: ExtractionItemLookup
  docNameLookup?: DocumentNameLookup
  onOpenSource?: OpenSourceFn
}

export function RequirementItemRow({
  id,
  description,
  status,
  isMandatory,
  category,
  sourceDocument,
  sourcePage,
  reasoning,
  gapDescription,
  suggestedFix,
  supportingSnippets,
  confidence,
  extractionLookup,
  docNameLookup,
  onOpenSource,
}: RequirementItemRowProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const conf = COMPLIANCE_CONFIG[status]
  const StatusIcon = conf.icon
  const hasDetails = Boolean(gapDescription || suggestedFix)
  const ChevronIcon = expanded ? ChevronDown : ChevronRight
  const hasEvidence = Boolean(reasoning) || (supportingSnippets?.length ?? 0) > 0
  const showSourceFallback = !hasEvidence && Boolean(sourceDocument)

  return (
    <div className={cn("rounded-lg border px-3 py-2.5", conf.rowClass)}>
      <div
        className={cn("flex items-start gap-3", hasDetails && "cursor-pointer")}
        role="presentation"
        onClick={() => hasDetails && setExpanded((v) => !v)}
      >
        <StatusIcon className={cn("mt-0.5 size-4 shrink-0", conf.iconClass)} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-foreground text-[12px] font-medium">{id}</span>
            <Badge
              className={cn("h-4 px-1.5 text-[10px] font-medium", conf.badgeClass)}
              variant="outline"
            >
              {t(conf.labelKey)}
            </Badge>
            {isMandatory && (
              <Badge
                className="border-border bg-muted/50 text-muted-foreground h-4 px-1.5 text-[10px] font-medium"
                variant="outline"
              >
                {t("tender.sections.extraction.tabs.krav")}
              </Badge>
            )}
            {category && (
              <Badge className="h-4 px-1.5 text-[10px] font-medium" variant="secondary">
                {category}
              </Badge>
            )}
          </div>
          {extractionLookup ? (
            <CitationText
              as="p"
              className="text-foreground mt-0.5 text-[12px] leading-snug"
              lookup={extractionLookup}
              text={description}
              onOpenSource={onOpenSource}
            />
          ) : (
            <p className="text-foreground mt-0.5 text-[12px] leading-snug">{description}</p>
          )}
          <div className="mt-1.5 flex items-center gap-2">
            {hasEvidence && (
              <div role="presentation" onClick={(e) => e.stopPropagation()}>
                <ComplianceEvidencePopover
                  confidence={confidence}
                  docNameLookup={docNameLookup}
                  extractionLookup={extractionLookup}
                  reasoning={reasoning}
                  supportingSnippets={supportingSnippets}
                  onOpenSource={onOpenSource}
                />
              </div>
            )}
            {showSourceFallback && (
              <SourceFallbackButton
                docNameLookup={docNameLookup}
                documentName={sourceDocument!}
                page={sourcePage}
                onOpenSource={onOpenSource}
              />
            )}
          </div>
        </div>
        {hasDetails && (
          <button
            aria-expanded={expanded}
            aria-label={`${t(expanded ? "tender.actions.minimize" : "tender.actions.expand")}: ${id}`}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -m-1.5 shrink-0 rounded p-1.5 focus-visible:ring-2 focus-visible:outline-none"
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setExpanded((v) => !v)
            }}
          >
            <ChevronIcon className="size-3.5" />
          </button>
        )}
      </div>
      {expanded && hasDetails && (
        <ComplianceItemDetails
          extractionLookup={extractionLookup}
          gapDescription={gapDescription}
          suggestedFix={suggestedFix}
          onOpenSource={onOpenSource}
        />
      )}
    </div>
  )
}

// ── Role item row ──────────────────────────────────────────────────────────────

export type RoleItemRowProps = {
  id: string
  roleName: string
  quantityRequired: number
  status: BidItemStatus
  reasoning?: string
  bidQuote?: string
  gapDescription?: string
  suggestedFix?: string
  supportingSnippets?: ComplianceSnippet[]
  confidence: number
  extractionLookup?: ExtractionItemLookup
  docNameLookup?: DocumentNameLookup
  onOpenSource?: OpenSourceFn
}

export function RoleItemRow({
  id,
  roleName,
  quantityRequired,
  status,
  reasoning,
  gapDescription,
  suggestedFix,
  supportingSnippets,
  confidence,
  extractionLookup,
  docNameLookup,
  onOpenSource,
}: RoleItemRowProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const conf = COMPLIANCE_CONFIG[status]
  const StatusIcon = conf.icon
  const hasDetails = Boolean(gapDescription || suggestedFix)
  const ChevronIcon = expanded ? ChevronDown : ChevronRight
  const hasEvidence = Boolean(reasoning) || (supportingSnippets?.length ?? 0) > 0

  return (
    <div className={cn("rounded-lg border px-3 py-2.5", conf.rowClass)}>
      <div
        className={cn("flex items-start gap-3", hasDetails && "cursor-pointer")}
        role="presentation"
        onClick={() => hasDetails && setExpanded((v) => !v)}
      >
        <StatusIcon className={cn("mt-0.5 size-4 shrink-0", conf.iconClass)} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-foreground text-[12px] font-medium">{id}</span>
            <Badge
              className={cn("h-4 px-1.5 text-[10px] font-medium", conf.badgeClass)}
              variant="outline"
            >
              {t(conf.labelKey)}
            </Badge>
          </div>
          <p className="text-foreground mt-0.5 text-[12px] leading-snug">
            {roleName}{" "}
            <span className="text-muted-foreground">
              ({t("tender.bidReview.quantityUnit", { count: quantityRequired })})
            </span>
          </p>
          {hasEvidence && (
            <div className="mt-1.5" role="presentation" onClick={(e) => e.stopPropagation()}>
              <ComplianceEvidencePopover
                confidence={confidence}
                docNameLookup={docNameLookup}
                extractionLookup={extractionLookup}
                reasoning={reasoning}
                supportingSnippets={supportingSnippets}
                onOpenSource={onOpenSource}
              />
            </div>
          )}
        </div>
        {hasDetails && (
          <button
            aria-expanded={expanded}
            aria-label={`${t(expanded ? "tender.actions.minimize" : "tender.actions.expand")}: ${roleName}`}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -m-1.5 shrink-0 rounded p-1.5 focus-visible:ring-2 focus-visible:outline-none"
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setExpanded((v) => !v)
            }}
          >
            <ChevronIcon className="size-3.5" />
          </button>
        )}
      </div>
      {expanded && hasDetails && (
        <ComplianceItemDetails
          extractionLookup={extractionLookup}
          gapDescription={gapDescription}
          suggestedFix={suggestedFix}
          onOpenSource={onOpenSource}
        />
      )}
    </div>
  )
}

// ── Deliverable item row ───────────────────────────────────────────────────────

export type DeliverableItemRowProps = {
  id: string
  deliverableName: string
  isRequired: boolean
  status: BidItemStatus
  reasoning?: string
  bidQuote?: string
  gapDescription?: string
  suggestedFix?: string
  supportingSnippets?: ComplianceSnippet[]
  confidence: number
  extractionLookup?: ExtractionItemLookup
  docNameLookup?: DocumentNameLookup
  onOpenSource?: OpenSourceFn
}

export function DeliverableItemRow({
  id,
  deliverableName,
  isRequired,
  status,
  reasoning,
  gapDescription,
  suggestedFix,
  supportingSnippets,
  confidence,
  extractionLookup,
  docNameLookup,
  onOpenSource,
}: DeliverableItemRowProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const conf = COMPLIANCE_CONFIG[status]
  const StatusIcon = conf.icon
  const hasDetails = Boolean(gapDescription || suggestedFix)
  const ChevronIcon = expanded ? ChevronDown : ChevronRight
  const hasEvidence = Boolean(reasoning) || (supportingSnippets?.length ?? 0) > 0

  return (
    <div className={cn("rounded-lg border px-3 py-2.5", conf.rowClass)}>
      <div
        className={cn("flex items-start gap-3", hasDetails && "cursor-pointer")}
        role="presentation"
        onClick={() => hasDetails && setExpanded((v) => !v)}
      >
        <StatusIcon className={cn("mt-0.5 size-4 shrink-0", conf.iconClass)} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-foreground text-[12px] font-medium">{id}</span>
            <Badge
              className={cn("h-4 px-1.5 text-[10px] font-medium", conf.badgeClass)}
              variant="outline"
            >
              {t(conf.labelKey)}
            </Badge>
            {isRequired && (
              <Badge
                className="border-border bg-muted/50 text-muted-foreground h-4 px-1.5 text-[10px] font-medium"
                variant="outline"
              >
                {t("tender.bidReview.mandatoryDeliverable")}
              </Badge>
            )}
          </div>
          <p className="text-foreground mt-0.5 text-[12px] leading-snug">{deliverableName}</p>
          {hasEvidence && (
            <div className="mt-1.5" role="presentation" onClick={(e) => e.stopPropagation()}>
              <ComplianceEvidencePopover
                confidence={confidence}
                docNameLookup={docNameLookup}
                extractionLookup={extractionLookup}
                reasoning={reasoning}
                supportingSnippets={supportingSnippets}
                onOpenSource={onOpenSource}
              />
            </div>
          )}
        </div>
        {hasDetails && (
          <button
            aria-expanded={expanded}
            aria-label={`${t(expanded ? "tender.actions.minimize" : "tender.actions.expand")}: ${deliverableName}`}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring -m-1.5 shrink-0 rounded p-1.5 focus-visible:ring-2 focus-visible:outline-none"
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setExpanded((v) => !v)
            }}
          >
            <ChevronIcon className="size-3.5" />
          </button>
        )}
      </div>
      {expanded && hasDetails && (
        <ComplianceItemDetails
          extractionLookup={extractionLookup}
          gapDescription={gapDescription}
          suggestedFix={suggestedFix}
          onOpenSource={onOpenSource}
        />
      )}
    </div>
  )
}
